// SettingsModal.jsx
import React from "react";
import {
  Box,
  Modal,
  Tabs,
  Tab,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useStore } from "@xyflow/react";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { CustomDropdown } from "../../../../components/shared/customDropDown";
import { CopyBlock, dracula } from "react-code-blocks";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { callAPI } from "../../../../functions/api";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
} from "@mui/material";

const MIN_SIDE_WIDTH = 100;
const SETTINGS_WIDTH = 600;

export const SettingsModal = ({ children, open, onClose, node }) => {
  // Early check to prevent issues with potentially problematic node data
  const safeNode = useMemo(() => {
    try {
      if (!node) return null;

      // Check for Excel export node type which might have special handling
      const isExcelExportNode = node.type === "export.excel" || node.data?.type === "export.excel";
      if (isExcelExportNode) {
        console.log("SettingsModal: Handling Excel export node", node.id);
        // Ensure the node has all required properties to prevent errors
        return {
          ...node,
          data: {
            ...node.data,
            model: node.data?.model || {},
            output: node.data?.output || {},
            type: node.data?.type || "export.excel",
          },
        };
      }

      return node;
    } catch (error) {
      console.error("Error in safeNode:", error);
      return node; // Return original as fallback
    }
  }, [node]);

  const nodes = useStore((s) => s.nodes);
  const edges = useStore((s) => s.edges);
  const containerRef = useRef(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [hoveringSettings, setHoveringSettings] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [offset, setOffset] = useState(0);
  const [inputNodes, setInputNodes] = useState([]);
  const [selectedInputId, setSelectedInputId] = useState("");
  const [selectedInput, setSelectedInput] = useState(null);
  const [nodeData, setNodeData] = useState(() => {
    try {
      return safeNode?.data || {};
    } catch (error) {
      console.error("Error initializing nodeData:", error);
      return {};
    }
  });
  const [isExecuting, setIsExecuting] = useState(false);
  const currentNode = useMemo(() => {
    try {
      return nodes.find((n) => n.id === safeNode?.id);
    } catch (error) {
      console.error("Error finding currentNode:", error);
      return null;
    }
  }, [nodes, safeNode?.id]);

  // Get preview data from either workflow execution or manual preview execution
  const preview = useMemo(() => {
    // Priority order for preview data
    const sampleData = currentNode?.data?.executionState?.sampleData;
    const nodeExecutionOutput = currentNode?.data?.executionState?.nodeExecutionOutput;

    // Return first available data source that can be used for table preview
    if (sampleData && Array.isArray(sampleData)) return sampleData;

    // Only use nodeExecutionOutput if it's an array (table data)
    if (nodeExecutionOutput && Array.isArray(nodeExecutionOutput)) return nodeExecutionOutput;

    return null;
  }, [currentNode?.data?.executionState]);
  const executionData = currentNode?.data?.executionState;

  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  const startDrag = (e) => {
    e.preventDefault();
    setDragging(true);
    setDragStartX(e.clientX);
  };
  useEffect(() => {
    try {
      setNodeData(safeNode?.data || {});
    } catch (error) {
      console.error("Error updating nodeData:", error);
    }
  }, [safeNode?.data]);

  const stopDrag = () => setDragging(false);

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging || !containerRef.current) return;
      requestAnimationFrame(() => {
        const { width } = containerRef.current.getBoundingClientRect();
        const maxOffset = (width - SETTINGS_WIDTH) / 2 - MIN_SIDE_WIDTH;
        const deltaX = e.clientX - dragStartX;
        const newOffset = Math.max(-maxOffset, Math.min(maxOffset, offset + deltaX));
        setOffset(newOffset);
        setDragStartX(e.clientX);
      });
    },
    [dragging, dragStartX, offset]
  );
  useEffect(() => {
    const inputNodeIds = edges.filter((edge) => edge.target === node.id).map((edge) => edge.source);
    const inputs = nodes.filter((n) => inputNodeIds.includes(n.id));

    setInputNodes(inputs);
    if (inputs.length > 0) {
      // Only auto-select if no input is currently selected or if current selection is no longer valid
      if (!selectedInputId || !inputs.find((input) => input.id === selectedInputId)) {
        setSelectedInputId(inputs[0].id);
        setSelectedInput(inputs[0]);
      } else {
        // Update the selected input object if it exists in the new inputs
        const currentInput = inputs.find((input) => input.id === selectedInputId);
        if (currentInput) {
          setSelectedInput(currentInput);
        }
      }
    }
  }, [node, edges, nodes, selectedInputId]);

  const memoizedJson = useMemo(() => {
    const nodeData = nodes.find((n) => n.id === selectedInputId)?.data.output || {};
    return JSON.stringify(nodeData, null, 2);
  }, [nodes, selectedInputId]);
  const memoizedChildren = useMemo(() => {
    const nodeWithLatestData = {
      ...node,
      data: currentNode?.data || node.data,
    };

    return React.cloneElement(children, {
      node: nodeWithLatestData,
      selectedInputId: selectedInputId, // Pass selectedInputId to child modals
    });
  }, [children, currentNode?.id, node, selectedInputId]);
  const executeNode = useCallback(async () => {
    if (isExecuting) return; // Prevent multiple simultaneous executions

    setIsExecuting(true);
    try {
      // Start both the API call and a minimum delay
      const nodeExecution = {
        id: currentNode.id,
        selectedNode: {
          output: JSON.stringify(currentNode.data.output),
          input: JSON.stringify(
            inputNodes.reduce((acc, inputNode) => {
              acc[inputNode.id] = inputNode.data?.output || {};
              return acc;
            }, {})
          ),
        },
      };

      const apiCall = callAPI(
        `${currentNode.data.execution}?preview=true`,
        "POST",
        nodeExecution,
        false
      );

      // Minimum 800ms delay to ensure spinner is visible
      const minDelay = new Promise((resolve) => setTimeout(resolve, 800));

      const [response] = await Promise.all([apiCall, minDelay]);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to execute node:", response.statusText, errorText);

        // Update the node with error information
        currentNode.data?.setData?.((old) => ({
          ...old,
          executionState: {
            ...(old.executionState || {}),
            error: `Execution failed: ${response.statusText}`,
            errorDetails: errorText,
          },
        }));
        return;
      }

      const data = await response.json();

      currentNode.data?.setData?.((old) => ({
        ...old,
        executionState: {
          ...(old.executionState || {}),
          preview: data,
          error: null, // Clear any previous errors
          errorDetails: null,
        },
      }));
    } catch (error) {
      console.error("Error executing node:", error);

      // Update the node with error information
      currentNode.data?.setData?.((old) => ({
        ...old,
        execution: {
          ...(old.execution || {}),
          error: `Execution error: ${error.message}`,
          errorDetails: error.toString(),
        },
      }));
    } finally {
      setIsExecuting(false);
    }
  }, [inputNodes, currentNode, isExecuting]);
  // Validation logic to determine if node can execute
  const canExecute = useMemo(() => {
    if (!currentNode) return false;
    const nodeData = currentNode.data;
    const nodeName = nodeData?.name;
    const requiredInputs = nodeData?.model?.inputs || [];

    const hasRequiredInputs = requiredInputs.length === 0 || inputNodes.length > 0;

    // Check if node has required settings and they are configured
    const settings = nodeData?.model?.settings || {};
    const requiredSettings = Object.values(settings).filter((s) => s.required);
    const hasRequiredSettings =
      requiredSettings.length === 0 ||
      requiredSettings.every((s) => s.selectedValue !== undefined && s.selectedValue !== "");

    // Check node-specific output settings if they exist
    const outputSettings = nodeData?.output?.settings || {};
    let hasOutputConfiguration = true;

    // For nodes with fieldsToInclude, check if at least one field is selected
    if (outputSettings.hasOwnProperty("fieldsToInclude")) {
      hasOutputConfiguration = (outputSettings.fieldsToInclude || []).length > 0;
    }

    // For nodes with selectedModel, check if model is selected
    if (outputSettings.hasOwnProperty("selectedModel")) {
      hasOutputConfiguration = !!outputSettings.selectedModel;
    }

    return hasRequiredInputs && hasRequiredSettings && hasOutputConfiguration;
  }, [currentNode, inputNodes]); // Get validation message for display
  const validationMessage = useMemo(() => {
    if (canExecute) return null;

    const nodeData = currentNode?.data;
    const requiredInputs = nodeData?.model?.inputs || [];

    // Check for missing inputs first
    if (requiredInputs.length > 0 && inputNodes.length === 0) {
      return "Connect input node(s) to execute";
    }

    // Check for output configuration issues
    const outputSettings = nodeData?.output?.settings || {};

    if (
      outputSettings.hasOwnProperty("fieldsToInclude") &&
      (!outputSettings.fieldsToInclude || outputSettings.fieldsToInclude.length === 0)
    ) {
      return "Select at least one field to include";
    }

    if (outputSettings.hasOwnProperty("selectedModel") && !outputSettings.selectedModel) {
      return "Select a model to execute";
    }

    // Check for required settings
    const settings = nodeData?.model?.settings || {};
    const requiredSettings = Object.values(settings).filter((s) => s.required);
    const missingSettings = requiredSettings.filter((s) => !s.selectedValue);

    if (missingSettings.length > 0) {
      return "Configure required settings to execute";
    }

    return "Configure node to execute";
  }, [canExecute, currentNode, inputNodes]);

  // Add this helper function near the top of the component, after the tableRows function

  const DownloadableContent = ({ content, title = "Generated File" }) => {
    // Parse content if it's a JSON string
    let parsedContent = content;
    console.log("----parse", parsedContent);
    if (typeof content === "string") {
      try {
        parsedContent = JSON.parse(content);
      } catch (error) {
        // If parsing fails, content is not downloadable
        console.error("Failed to parse content as JSON:", error);
        return null;
      }
    }

    console.log(parsedContent);

    // Check if parsed content has downloadable properties
    const isDownloadable =
      parsedContent &&
      typeof parsedContent === "object" &&
      parsedContent.downloadUrl &&
      parsedContent.filename;

    if (!isDownloadable) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ color: "#aaa", fontWeight: "bold", mb: 1 }}>{title}</Typography>
        <Box
          sx={{
            backgroundColor: "#1e4a1e",
            border: "1px solid #4caf50",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography sx={{ color: "#4caf50", fontSize: "14px", fontWeight: "bold" }}>
              📄 {parsedContent.message || "File ready for download"}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: "#ccc", fontSize: "12px", mb: 1 }}>
              <strong>Filename:</strong> {parsedContent.filename}
            </Typography>
            {parsedContent.size && (
              <Typography sx={{ color: "#ccc", fontSize: "12px", mb: 1 }}>
                <strong>Size:</strong> {parsedContent.size}
              </Typography>
            )}
            {parsedContent.recordCount && (
              <Typography sx={{ color: "#ccc", fontSize: "12px", mb: 1 }}>
                <strong>Records:</strong> {parsedContent.recordCount}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={() => window.open(parsedContent.downloadUrl, "_blank")}
            sx={{
              backgroundColor: "#4caf50",
              fontSize: "12px",
              "&:hover": { backgroundColor: "#45a049" },
            }}
          >
            📥 Download {parsedContent.filename?.split(".").pop()?.toUpperCase() || "File"}
          </Button>
        </Box>
      </Box>
    );
  };

  const tableRows = ({ rows }) => {
    if (!Array.isArray(rows)) {
      if (rows.length === 0) {
        return null;
      }

      return (
        <CopyBlock
          text={JSON.stringify(rows, null, 2)}
          language="json"
          theme={dracula}
          showLineNumbers={false}
          wrapLongLines={true}
          codeBlock={{
            copyText: "Copy",
            copySuccessText: "Copied!",
          }}
        />
      );
    }

    const columns = Object.keys(rows[0]);
    return (
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#2e2e2e" }}>
              {columns.map((col) => (
                <TableCell key={col} sx={{ color: "#fff" }}>
                  {col.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                hover
                sx={{
                  height: "40px",
                  backgroundColor: rowIndex % 2 === 0 ? "#1e1e1e" : "#2c2c2c",
                  "&:hover": { backgroundColor: "#444" },
                  transition: "background 0.2s ease",
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      color: "#ddd",
                      height: "40px",
                      padding: "4px 8px",
                      lineHeight: "1.2",
                      verticalAlign: "middle",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {typeof row[col] === "object" ? JSON.stringify(row[col]) : String(row[col])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(71, 69, 84, 0.75)", // backdrop opacity
        },
      }}
    >
      <Box
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        sx={{
          padding: "60px 20px",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "row",
          userSelect: dragging ? "none" : "auto",
          cursor: dragging ? "grabbing" : "default",
        }}
      >
        <Box
          onClick={onClose}
          sx={{ cursor: "pointer", position: "absolute", top: "5px", left: "5px" }}
        >
          <IconButton
            size="small"
            sx={{
              ml: 1,
              color: "#ccc",

              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <HighlightOffIcon />
          </IconButton>
        </Box>{" "}
        {/* INPUT */}
        {currentNode.data?.model?.inputs?.length > 0 && (
          <Box
            sx={{
              width: `calc((100% - ${SETTINGS_WIDTH}px) / 2 + ${offset}px)`,
              minWidth: MIN_SIDE_WIDTH,
              backgroundColor: "#333",
              borderTopLeftRadius: "10px",
              borderBottomLeftRadius: "10px",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ color: "#aaa", fontWeight: "bold" }}>INPUT</Typography>

              {selectedInput?.type && !selectedInput.type.includes("trigger") && (
                <Box sx={{ backgroundColor: "#414243", px: 1, borderRadius: 1 }}>
                  <Typography sx={{ color: "#fff", fontSize: 12 }}>JSON</Typography>
                </Box>
              )}
            </Box>

            {inputNodes?.length > 1 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: "#aaa", fontSize: "12px", mb: 1, fontWeight: "500" }}>
                  Data Source ({inputNodes.length} available)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {inputNodes.map((inputNode) => (
                    <Box
                      key={inputNode.id}
                      onClick={() => setSelectedInputId(inputNode.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        backgroundColor: selectedInputId === inputNode.id ? "#FF6C58" : "#2a2a2a",
                        border:
                          selectedInputId === inputNode.id ? "1px solid #FF6C58" : "1px solid #444",
                        color: selectedInputId === inputNode.id ? "#fff" : "#ccc",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        fontSize: "13px",
                        fontWeight: "500",
                        "&:hover": {
                          backgroundColor: selectedInputId === inputNode.id ? "#e55a4a" : "#3a3a3a",
                          borderColor: selectedInputId === inputNode.id ? "#e55a4a" : "#555",
                        },
                      }}
                    >
                      {/* Connection indicator dot */}
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: selectedInputId === inputNode.id ? "#fff" : "#FF6C58",
                          flexShrink: 0,
                        }}
                      />

                      {/* Node label/name */}
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "inherit",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          maxWidth: "150px",
                        }}
                      >
                        {inputNode.data?.label ||
                          inputNode.data?.name ||
                          `Node ${inputNode.id.slice(-4)}`}
                      </Typography>

                      {/* Node type badge */}
                      {inputNode.data?.type && (
                        <Typography
                          sx={{
                            fontSize: "10px",
                            px: 0.8,
                            py: 0.2,
                            borderRadius: "4px",
                            backgroundColor:
                              selectedInputId === inputNode.id
                                ? "rgba(255,255,255,0.2)"
                                : "rgba(255,108,88,0.2)",
                            color: selectedInputId === inputNode.id ? "#fff" : "#FF6C58",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {inputNode.data.type.split(".").pop()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                backgroundColor: "#1e1e1e",
                color: "#d4d4d4",
                fontFamily: "monospace",
                fontSize: "14px",
                p: 2,
                borderRadius: 2,

                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {inputNodes.length > 0 &&
              selectedInput != null &&
              selectedInput.type?.indexOf("trigger") < 0 ? (
                <CopyBlock
                  text={memoizedJson}
                  language="json"
                  theme={dracula}
                  showLineNumbers={false}
                  wrapLongLines={true}
                  codeBlock={{
                    copyText: "Copy",
                    copySuccessText: "Copied!",
                  }}
                />
              ) : inputNodes.length > 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    padding: 2,
                    display: "flex",
                    height: "calc(100vh - 232px)",
                    flexDirection: "column",
                    alignItems: "center",
                    overflow: "hidden",
                    justifyContent: "center",
                  }}
                >
                  {selectedInput?.type?.indexOf("trigger") >= 0 ? (
                    <Typography sx={{ color: "#888" }}>
                      This node is a trigger node and does not have input data.
                    </Typography>
                  ) : (
                    <Typography sx={{ color: "#888" }}>No input data available</Typography>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    padding: 2,
                    display: "flex",
                    height: "calc(100vh - 232px)",
                    flexDirection: "column",
                    alignItems: "center",
                    overflow: "hidden",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: "#888" }}>Connect Input Node</Typography>
                  <Typography sx={{ color: "#888", fontSize: 12 }}>
                    You must connect a node to to this node to see input data.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {/* SETTINGS */}
        <Box
          onMouseEnter={() => setHoveringSettings(true)}
          onMouseLeave={() => setHoveringSettings(false)}
          sx={{
            width: SETTINGS_WIDTH,
            backgroundColor: "#414243",
            borderRadius: "10px",

            height: "calc(100% + 50px)",
            position: "relative",
            display: "flex",
            top: "-25px",
            padding: 0,
            flexDirection: "column",
            border: dragging ? "2px solid #EF4E39" : "2px solid #535456",
            transition: dragging ? "none" : "border 0.2s",
          }}
        >
          {node.data?.model?.inputs?.length > 0 && (
            <Box
              onMouseDown={startDrag}
              sx={{
                position: "absolute",
                height: 20,
                width: 60,
                left: 265,
                top: -12,
                borderRadius: "10px",
                backgroundColor: "#535456",
                cursor: dragging ? "grabbing" : "grab",
                opacity: hoveringSettings ? 1 : 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "opacity 0.2s",
              }}
            >
              <Typography sx={{ color: "#aaa", fontSize: 18, userSelect: "none" }}>⋮⋮⋮</Typography>
            </Box>
          )}
          <Box
            sx={{
              backgroundColor: "#535456",
              display: "flex",
              alignItems: "center",
              p: 2,
              borderTopRightRadius: "10px",
              borderTopLeftRadius: "10px",
            }}
          >
            {node.data?.icon && (
              <Box sx={{ mr: 1 }}>
                <img
                  src={require(`../../../../assets/${node.data.icon}.png`)}
                  alt="Node Icon"
                  style={{ width: 20, height: 20, borderRadius: "50%" }}
                />
              </Box>
            )}{" "}
            <Typography
              sx={{
                color: "#fff",
                fontWeight: "bold",
                fontSize: 16,
                display: "flex",
                flex: 1,
                alignItems: "center",
              }}
            >
              {" "}
              {node?.data?.label || "Node"}
            </Typography>
            <Box>
              {/* Execute button - show spinner when executing */}
              {canExecute && (
                <Button
                  sx={{
                    backgroundColor: isExecuting ? "rgba(255, 111, 92, 0.7)" : "rgb(255, 111, 92)",
                    padding: "5px 10px",
                    cursor: isExecuting ? "wait" : "pointer",
                    "&:hover": {
                      backgroundColor: isExecuting
                        ? "rgba(255, 111, 92, 0.7)"
                        : "rgb(230, 100, 82)",
                    },
                  }}
                  onClick={executeNode}
                  variant="contained"
                  size="small"
                  disabled={isExecuting}
                  startIcon={
                    isExecuting ? (
                      <CircularProgress size={16} thickness={4} sx={{ color: "#fff" }} />
                    ) : (
                      <svg
                        className="svg-inline--fa fa-flask fa-w-14"
                        aria-hidden="true"
                        focusable="false"
                        data-prefix="fas"
                        data-icon="flask"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        style={{ width: 12, height: 12 }}
                      >
                        <path
                          fill="currentColor"
                          d="M437.2 403.5L320 215V64h8c13.3 0 24-10.7 24-24V24c0-13.3-10.7-24-24-24H120c-13.3 0-24 10.7-24 24v16c0 13.3 10.7 24 24 24h8v151L10.8 403.5C-18.5 450.6 15.3 512 70.9 512h306.2c55.7 0 89.4-61.5 60.1-108.5zM137.9 320l48.2-77.6c3.7-5.2 5.8-11.6 5.8-18.4V64h64v160c0 6.9 2.2 13.2 5.8 18.4l48.2 77.6h-172z"
                        />
                      </svg>
                    )
                  }
                >
                  <Typography sx={{ fontSize: 12, color: "#fff" }}>
                    {isExecuting ? "Executing..." : "Execute"}
                  </Typography>
                </Button>
              )}
              {/* Show validation message when node can't execute */}
              {!canExecute && !isExecuting && (
                <Typography sx={{ fontSize: 11, color: "#ff9800", textAlign: "right" }}>
                  {validationMessage}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ backgroundColor: "#535456", display: "flex" }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              TabIndicatorProps={{ style: { backgroundColor: "#FF6C58", height: 3 } }}
              sx={{ minHeight: 32, height: 32 }}
            >
              <Tab
                label="Settings"
                sx={{
                  fontSize: 14,

                  color: "#fff",
                  textTransform: "none",
                  minHeight: 32,
                  height: 32,
                  "&.Mui-selected": { color: "#FF6C58" },
                }}
              />
              <Tab
                label="Options"
                sx={{
                  fontSize: 14,
                  color: "#fff",
                  textTransform: "none",
                  minHeight: 32,
                  height: 32,
                  "&.Mui-selected": { color: "#FF6C58" },
                }}
              />
              {/* Show Execution tab if node has been executed or has execution data */}
              {(currentNode.data?.execution?.success ||
                currentNode.data?.execution?.sampleData) && (
                <Tab
                  label="Execution"
                  sx={{
                    fontSize: 14,
                    color: "#fff",
                    textTransform: "none",
                    minHeight: 32,
                    height: 32,
                    "&.Mui-selected": { color: "#FF6C58" },
                  }}
                />
              )}
            </Tabs>
          </Box>

          <Box sx={{ p: 2, flexGrow: 1, overflowY: "auto" }}>
            {tabIndex === 0 && <>{memoizedChildren}</>}
            {tabIndex === 1 && <Typography>Options content goes here</Typography>}
            {tabIndex === 2 &&
              (currentNode.data?.execution?.success || currentNode.data?.execution?.sampleData) && (
                <Box>
                  <Typography
                    sx={{ mb: 2, color: "#70c0ff", fontWeight: "bold", fontSize: "18px" }}
                  >
                    🚀 Execution Results
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ mb: 1, color: "#fff", fontSize: "14px" }}>
                      Execution Details:
                    </Typography>
                    <Box sx={{ backgroundColor: "#2a2a2a", p: 2, borderRadius: "4px" }}>
                      {executionData?.lastExecuted && (
                        <Typography sx={{ color: "#b0b0b0", fontSize: "12px", mb: 1 }}>
                          <strong>Last Executed:</strong>{" "}
                          {new Date(executionData.lastExecuted).toLocaleString()}
                        </Typography>
                      )}
                      <Typography sx={{ color: "#b0b0b0", fontSize: "12px", mb: 1 }}>
                        <strong>Status:</strong>
                        <span
                          style={{
                            color: executionData?.success ? "#4caf50" : "#f44336",
                            marginLeft: "8px",
                          }}
                        >
                          {executionData?.success ? "✅ Success" : "❌ Failed"}
                        </span>
                      </Typography>
                      {executionData?.recordCount && (
                        <Typography sx={{ color: "#b0b0b0", fontSize: "12px", mb: 1 }}>
                          <strong>Records:</strong> {executionData.recordCount}
                        </Typography>
                      )}
                      {executionData?.step && (
                        <Typography sx={{ color: "#b0b0b0", fontSize: "12px", mb: 1 }}>
                          <strong>Step:</strong> {executionData.step}
                        </Typography>
                      )}
                      {currentNode.statusMessage && (
                        <Typography sx={{ color: "#b0b0b0", fontSize: "12px", mb: 1 }}>
                          <strong>Message:</strong> {currentNode.statusMessage}
                        </Typography>
                      )}
                      {executionData?.error && (
                        <Typography sx={{ color: "#f44336", fontSize: "12px", fontWeight: "bold" }}>
                          <strong>Error:</strong> {executionData.error}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Show sample data from workflow execution */}
                  {executionData?.sampleData && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ mb: 1, color: "#fff", fontSize: "14px" }}>
                        Sample Data:
                      </Typography>
                      <CopyBlock
                        text={JSON.stringify(executionData.sampleData, null, 2)}
                        language="json"
                        theme={dracula}
                        showLineNumbers={false}
                        codeBlock={{
                          style: {
                            fontSize: "12px",
                            maxHeight: "300px",
                            overflowY: "auto",
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Show node execution output from workflow execution */}
                  {executionData?.nodeExecutionOutput && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ mb: 1, color: "#fff", fontSize: "14px" }}>
                        Node Execution Output:
                      </Typography>
                      {(() => {
                        const output = executionData.nodeExecutionOutput;

                        // Handle string output (simple messages like "850 Records")
                        if (typeof output === "string") {
                          return (
                            <Box
                              sx={{
                                backgroundColor: "#1e1e1e",
                                color: "#d4d4d4",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid #444",
                              }}
                            >
                              {output}
                            </Box>
                          );
                        }

                        // Handle object output (like Excel export info)
                        if (typeof output === "object" && output !== null) {
                          // Special handling for file download objects
                          if (output.downloadUrl && output.filename) {
                            return (
                              <Box>
                                <Box
                                  sx={{
                                    backgroundColor: "#1e1e1e",
                                    p: 2,
                                    borderRadius: 2,
                                    border: "1px solid #4caf50",
                                    mb: 2,
                                  }}
                                >
                                  <Typography sx={{ color: "#4caf50", fontSize: "12px", mb: 1 }}>
                                    📄 File Generated Successfully
                                  </Typography>
                                  <Typography sx={{ color: "#ccc", fontSize: "12px", mb: 1 }}>
                                    <strong>Filename:</strong> {output.filename}
                                  </Typography>
                                  <Typography sx={{ color: "#ccc", fontSize: "12px", mb: 2 }}>
                                    <strong>Message:</strong> {output.message}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => window.open(output.downloadUrl, "_blank")}
                                    sx={{
                                      backgroundColor: "#4caf50",
                                      fontSize: "11px",
                                      "&:hover": { backgroundColor: "#45a049" },
                                    }}
                                  >
                                    📥 Download File
                                  </Button>
                                </Box>
                                <CopyBlock
                                  text={JSON.stringify(output, null, 2)}
                                  language="json"
                                  theme={dracula}
                                  showLineNumbers={false}
                                  codeBlock={{
                                    style: {
                                      fontSize: "11px",
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                    },
                                  }}
                                />
                              </Box>
                            );
                          }

                          // Default object display
                          return (
                            <CopyBlock
                              text={JSON.stringify(output, null, 2)}
                              language="json"
                              theme={dracula}
                              showLineNumbers={false}
                              codeBlock={{
                                style: {
                                  fontSize: "12px",
                                  maxHeight: "300px",
                                  overflowY: "auto",
                                },
                              }}
                            />
                          );
                        }

                        // Fallback for other types
                        return (
                          <Box
                            sx={{
                              backgroundColor: "#1e1e1e",
                              color: "#d4d4d4",
                              fontFamily: "monospace",
                              fontSize: "14px",
                              p: 2,
                              borderRadius: 2,
                              border: "1px solid #444",
                            }}
                          >
                            {String(output)}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Legacy output data (from manual preview) */}
                  {executionData?.output && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ mb: 1, color: "#fff", fontSize: "14px" }}>
                        Output Data (Manual Preview):
                      </Typography>
                      <CopyBlock
                        text={JSON.stringify(executionData.output, null, 2)}
                        language="json"
                        theme={dracula}
                        showLineNumbers={false}
                        codeBlock={{
                          style: {
                            fontSize: "12px",
                            maxHeight: "300px",
                            overflowY: "auto",
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Legacy result data (from manual preview) */}
                  {executionData?.result && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ mb: 1, color: "#fff", fontSize: "14px" }}>
                        Full API Response (Manual Preview):
                      </Typography>
                      <CopyBlock
                        text={JSON.stringify(executionData.result, null, 2)}
                        language="json"
                        theme={dracula}
                        showLineNumbers={false}
                        codeBlock={{
                          style: {
                            fontSize: "11px",
                            maxHeight: "200px",
                            overflowY: "auto",
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
          </Box>
        </Box>
        {/* OUTPUT */}
        <Box
          sx={{
            width:
              currentNode.data?.model?.inputs?.length > 0
                ? `calc((100% - ${SETTINGS_WIDTH}px)  / 2 - ${offset}px)`
                : `calc((100% - ${SETTINGS_WIDTH}px)  - ${offset}px)`,
            minWidth: MIN_SIDE_WIDTH,
            backgroundColor: "#333",
            borderTopRightRadius: 2,
            borderBottomRightRadius: 2,
            padding: 2,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ color: "#aaa", fontWeight: "bold" }}>OUTPUT</Typography>
          </Box>

          {/* Query Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: currentNode.data?.output ? 1 : 0,
              minHeight: currentNode.data?.output ? 0 : "auto",
              mb: 2,
            }}
          >
            <Typography sx={{ color: "#aaa", fontWeight: "bold", mb: 1 }}>Query</Typography>
            <Box
              sx={{
                backgroundColor: "#1e1e1e",
                color: "#d4d4d4",
                fontFamily: "monospace",
                fontSize: "14px",
                p: 2,
                borderRadius: 2,
                overflow: "hidden",
                flex: 1,
                minHeight: currentNode.data?.output ? 0 : "200px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {currentNode.data?.output ? (
                <Box
                  sx={{
                    height: "100%",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    "& .react-code-block": {
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    },
                    "& .react-code-block code": {
                      flex: 1,
                      overflowY: "auto",
                    },
                  }}
                >
                  <CopyBlock
                    text={JSON.stringify(currentNode.data.output, null, 2)}
                    language="json"
                    theme={dracula}
                    showLineNumbers={false}
                    wrapLongLines={true}
                    codeBlock={{
                      copyText: "Copy",
                      copySuccessText: "Copied!",
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    padding: 2,
                    display: "flex",
                    height: "100%",
                    flexDirection: "column",
                    alignItems: "center",
                    overflow: "hidden",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: "#888" }}>No output data available</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Preview Section - only show if preview data exists */}
          {preview && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                mb: 2,
              }}
            >
              <Typography sx={{ color: "#aaa", fontWeight: "bold", mb: 1 }}>
                Execution Preview
                {currentNode?.data?.executionState?.sampleData && (
                  <Typography sx={{ color: "#4caf50", fontSize: "12px", ml: 1, display: "inline" }}>
                    (From Workflow Execution)
                  </Typography>
                )}
              </Typography>

              {currentNode.data?.executionState?.recordCount && (
                <Typography sx={{ color: "#888", fontSize: "12px", mb: 1 }}>
                  Total records: {currentNode.data.executionState.recordCount}
                </Typography>
              )}

              <Box
                sx={{
                  backgroundColor: "#1e1e1e",
                  borderRadius: 2,
                  overflow: "hidden",
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ overflowY: "auto", flex: 1, p: 1 }}>{tableRows({ rows: preview })}</Box>
              </Box>
            </Box>
          )}

          {/* Error Section - only show if error exists */}
          {currentNode.data?.execution?.error && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: preview ? 0 : 1,
                minHeight: preview ? "auto" : 0,
              }}
            >
              <Typography sx={{ color: "#ff6b6b", fontWeight: "bold", mb: 1 }}>
                Execution Error
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#2d1b1b",
                  border: "1px solid #ff6b6b",
                  borderRadius: 1,
                  p: 2,
                  flex: 1,
                  overflowY: "auto",
                  minHeight: preview ? "150px" : 0,
                }}
              >
                <Typography sx={{ color: "#ff9999", fontSize: "14px", mb: 1 }}>
                  {currentNode.data.execution.error}
                </Typography>
                {currentNode.data.execution.errorDetails && (
                  <Typography
                    sx={{
                      color: "#cc8888",
                      fontSize: "12px",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {currentNode.data.execution.errorDetails}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Download Section - only show if downloadable content exists */}
          <DownloadableContent
            content={executionData?.nodeExecutionOutput}
            title="Execution Output"
          />
        </Box>
      </Box>
    </Modal>
  );
};
